import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import axios from 'axios';
import { ChevronRight, Check } from 'lucide-react';

const EnhancedOnboarding = () => {
 const { API } = useContext(AuthContext);
 const navigate = useNavigate();
 const [step, setStep] = useState(1);
 const [options, setOptions] = useState({});
 const [formData, setFormData] = useState({
 first_name: '',
 last_name: '',
 city: '',
 state: '',
 country: '',
 postal_code: '',
 how_heard: '',
 experience_level: '',
 main_interests: [],
 freeze_dryer: {
 brand: '',
 model_size: '',
 purchase_date: '',
 primary_use: '',
 ownership_status: '',
 notes: ''
 }
 });
 const [loading, setLoading] = useState(false);

 useEffect(() => {
 fetchOptions();
 }, []);

 const fetchOptions = async () => {
 try {
 const response = await axios.get(`${API}/gamification/options`);
 setOptions(response.data);
 } catch (error) {
 console.error('Failed to fetch options:', error);
 }
 };

 const handleInterestToggle = (interest) => {
 setFormData(prev => ({
 ...prev,
 main_interests: prev.main_interests.includes(interest)
 ? prev.main_interests.filter(i => i !== interest)
 : [...prev.main_interests, interest]
 }));
 };

 const handleSubmit = async (e) => {
 e.preventDefault();
 
 // Validation
 if (step === 1) {
 if (!formData.first_name || !formData.last_name || !formData.country || !formData.state) {
 toast.error('Please fill in all required fields');
 return;
 }
 setStep(2);
 return;
 }

 if (step === 2) {
 if (!formData.freeze_dryer.ownership_status || !formData.freeze_dryer.brand || !formData.freeze_dryer.model_size || !formData.freeze_dryer.primary_use) {
 toast.error('Please complete the equipment information');
 return;
 }
 setStep(3);
 return;
 }

 if (step === 3) {
 if (!formData.experience_level || formData.main_interests.length === 0) {
 toast.error('Please select your experience level and at least one interest');
 return;
 }
 }

 // Submit all data
 setLoading(true);
 try {
 await axios.post(`${API}/onboarding/complete`, formData);
 toast.success('Welcome to Freeze Dry Academy!');
 navigate('/dashboard');
 } catch (error) {
 toast.error('Failed to complete onboarding');
 console.error(error);
 } finally {
 setLoading(false);
 }
 };

 const progressPercent = (step / 3) * 100;

 return (
 <div className="min-h-screen bg-background dark:from-neutral-900 dark:to-neutral-800 py-12 px-4">
 <div className="max-w-3xl mx-auto">
 {/* Progress Bar */}
 <div className="mb-8">
 <div className="flex justify-between mb-2">
 <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Step {step} of 3</span>
 <span className="text-sm font-medium text-slate-700 dark:text-slate-300">{Math.round(progressPercent)}%</span>
 </div>
 <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2.5">
 <div 
 className="bg-primary text-primary-foreground h-2.5 rounded-full transition-all duration-500" 
 style={{ width: `${progressPercent}%` }}
 />
 </div>
 </div>

 <Card className="shadow-2xl border-0">
 <CardHeader className="text-center pb-8">
 <CardTitle className="text-3xl sm:text-4xl font-bold bg-primary text-primary-foreground bg-clip-text text-transparent">
 {step === 1 && 'Welcome to Freeze Dry Academy!'}
 {step === 2 && 'Tell Us About Your Equipment'}
 {step === 3 && 'Your Freeze Drying Journey'}
 </CardTitle>
 <CardDescription className="text-base">
 {step === 1 && "Let's get to know you better"}
 {step === 2 && 'Help us understand your setup'}
 {step === 3 && 'Almost there! Just a few more details'}
 </CardDescription>
 </CardHeader>
 
 <CardContent>
 <form onSubmit={handleSubmit} className="space-y-6">
 {/* Step 1: Personal Information */}
 {step === 1 && (
 <div className="space-y-4 animate-in fade-in slide-in-from-bottom-4 duration-500">
 {/* Info Banner */}
 <div className="bg-blue-50 dark:bg-blue-900/20 border-2 border-blue-200 dark:border-blue-800 rounded-lg p-4">
 <p className="text-sm text-blue-900 dark:text-blue-100">
 <span className="font-semibold">💡 Why we ask:</span> The more details you provide, the better our AI assistant can personalize freeze-drying recommendations, troubleshooting tips, and connect you with nearby community members!
 </p>
 </div>

 <div className="grid grid-cols-2 gap-4">
 <div className="space-y-2">
 <Label className="text-sm font-semibold">First Name *</Label>
 <Input
 value={formData.first_name}
 onChange={(e) => setFormData({...formData, first_name: e.target.value})}
 placeholder="John"
 required
 className="border-2"
 />
 </div>
 <div className="space-y-2">
 <Label className="text-sm font-semibold">Last Name *</Label>
 <Input
 value={formData.last_name}
 onChange={(e) => setFormData({...formData, last_name: e.target.value})}
 placeholder="Doe"
 required
 className="border-2"
 />
 </div>
 </div>

 <div className="space-y-2">
 <Label className="text-sm font-semibold">Country *</Label>
 <Select value={formData.country} onValueChange={(v) => setFormData({...formData, country: v, state: ''})} required>
 <SelectTrigger className="border-2">
 <SelectValue placeholder="Select your country..." />
 </SelectTrigger>
 <SelectContent>
 {options.countries?.map((country) => (
 <SelectItem key={country} value={country}>{country}</SelectItem>
 ))}
 </SelectContent>
 </Select>
 </div>

 <div className="grid grid-cols-2 gap-4">
 <div className="space-y-2">
 <Label className="text-sm font-semibold">State/Province *</Label>
 <Select value={formData.state} onValueChange={(v) => setFormData({...formData, state: v})} required disabled={!formData.country}>
 <SelectTrigger className="border-2">
 <SelectValue placeholder={formData.country ? "Select..." : "Select country first"} />
 </SelectTrigger>
 <SelectContent>
 {formData.country === 'United States' && options.us_states?.map((state) => (
 <SelectItem key={state} value={state}>{state}</SelectItem>
 ))}
 {formData.country === 'Canada' && options.canadian_provinces?.map((province) => (
 <SelectItem key={province} value={province}>{province}</SelectItem>
 ))}
 </SelectContent>
 </Select>
 </div>
 <div className="space-y-2">
 <Label className="text-sm font-semibold">City</Label>
 <Input
 value={formData.city}
 onChange={(e) => setFormData({...formData, city: e.target.value})}
 placeholder="Los Angeles"
 className="border-2"
 />
 </div>
 </div>

 <div className="space-y-2">
 <Label className="text-sm font-semibold">{formData.country === 'Canada' ? 'Postal Code' : 'ZIP Code'}</Label>
 <Input
 value={formData.postal_code}
 onChange={(e) => setFormData({...formData, postal_code: e.target.value})}
 placeholder={formData.country === 'Canada' ? 'A1A 1A1' : '90210'}
 className="border-2"
 />
 <p className="text-xs text-muted-foreground">Helps our AI provide region-specific climate and altitude adjustments</p>
 </div>

 <div className="space-y-2">
 <Label className="text-sm font-semibold">How did you hear about us? *</Label>
 <Select value={formData.how_heard} onValueChange={(v) => setFormData({...formData, how_heard: v})} required>
 <SelectTrigger className="border-2">
 <SelectValue placeholder="Select..." />
 </SelectTrigger>
 <SelectContent>
 {options.how_heard?.map((option) => (
 <SelectItem key={option} value={option}>{option}</SelectItem>
 ))}
 </SelectContent>
 </Select>
 </div>
 </div>
 )}

 {/* Step 2: Equipment Information */}
 {step === 2 && (
 <div className="space-y-4 animate-in fade-in slide-in-from-bottom-4 duration-500">
 <div className="space-y-2">
 <Label className="text-sm font-semibold">Do you own a freeze dryer? *</Label>
 <Select 
 value={formData.freeze_dryer.ownership_status} 
 onValueChange={(v) => setFormData({...formData, freeze_dryer: {...formData.freeze_dryer, ownership_status: v}})}
 required
 >
 <SelectTrigger className="border-2">
 <SelectValue placeholder="Select..." />
 </SelectTrigger>
 <SelectContent>
 <SelectItem value="Own">Yes, I own one</SelectItem>
 <SelectItem value="Ordered">I&apos;ve ordered one</SelectItem>
 <SelectItem value="Planning to Buy">Planning to buy</SelectItem>
 </SelectContent>
 </Select>
 </div>

 <div className="space-y-2">
 <Label className="text-sm font-semibold">Brand *</Label>
 <Select 
 value={formData.freeze_dryer.brand} 
 onValueChange={(v) => setFormData({...formData, freeze_dryer: {...formData.freeze_dryer, brand: v}})}
 required
 >
 <SelectTrigger className="border-2">
 <SelectValue placeholder="Select brand..." />
 </SelectTrigger>
 <SelectContent>
 {options.brands?.map((brand) => (
 <SelectItem key={brand} value={brand}>{brand}</SelectItem>
 ))}
 </SelectContent>
 </Select>
 </div>

 <div className="space-y-2">
 <Label className="text-sm font-semibold">Model/Size *</Label>
 <Select 
 value={formData.freeze_dryer.model_size} 
 onValueChange={(v) => setFormData({...formData, freeze_dryer: {...formData.freeze_dryer, model_size: v}})}
 required
 >
 <SelectTrigger className="border-2">
 <SelectValue placeholder="Select size..." />
 </SelectTrigger>
 <SelectContent>
 {options.model_sizes?.map((size) => (
 <SelectItem key={size} value={size}>{size}</SelectItem>
 ))}
 </SelectContent>
 </Select>
 </div>

 <div className="space-y-2">
 <Label className="text-sm font-semibold">Purchase Date {formData.freeze_dryer.ownership_status === 'Own' && '*'}</Label>
 <Input
 type="date"
 value={formData.freeze_dryer.purchase_date}
 onChange={(e) => setFormData({...formData, freeze_dryer: {...formData.freeze_dryer, purchase_date: e.target.value}})}
 className="border-2"
 />
 </div>

 <div className="space-y-2">
 <Label className="text-sm font-semibold">Primary Use *</Label>
 <Select 
 value={formData.freeze_dryer.primary_use} 
 onValueChange={(v) => setFormData({...formData, freeze_dryer: {...formData.freeze_dryer, primary_use: v}})}
 required
 >
 <SelectTrigger className="border-2">
 <SelectValue placeholder="Select use..." />
 </SelectTrigger>
 <SelectContent>
 {options.primary_uses?.map((use) => (
 <SelectItem key={use} value={use}>{use}</SelectItem>
 ))}
 </SelectContent>
 </Select>
 </div>

 <div className="space-y-2">
 <Label className="text-sm font-semibold">Notes (Optional)</Label>
 <Input
 value={formData.freeze_dryer.notes}
 onChange={(e) => setFormData({...formData, freeze_dryer: {...formData.freeze_dryer, notes: e.target.value}})}
 placeholder="Any additional details..."
 className="border-2"
 />
 </div>
 </div>
 )}

 {/* Step 3: Experience & Interests */}
 {step === 3 && (
 <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
 <div className="space-y-2">
 <Label className="text-sm font-semibold">Experience Level *</Label>
 <Select 
 value={formData.experience_level} 
 onValueChange={(v) => setFormData({...formData, experience_level: v})}
 required
 >
 <SelectTrigger className="border-2">
 <SelectValue placeholder="Select level..." />
 </SelectTrigger>
 <SelectContent>
 {options.experience_levels?.map((level) => (
 <SelectItem key={level} value={level}>{level}</SelectItem>
 ))}
 </SelectContent>
 </Select>
 </div>

 <div className="space-y-3">
 <Label className="text-sm font-semibold">Main Interests * (Select all that apply)</Label>
 <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
 {options.interests?.map((interest) => (
 <div key={interest} className="flex items-center space-x-2 p-3 border-2 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors cursor-pointer"
 onClick={() => handleInterestToggle(interest)}
 >
 <Checkbox
 id={interest}
 checked={formData.main_interests.includes(interest)}
 onCheckedChange={() => handleInterestToggle(interest)}
 />
 <label htmlFor={interest} className="text-sm cursor-pointer flex-1">
 {interest}
 </label>
 {formData.main_interests.includes(interest) && <Check className="w-4 h-4 text-green-600" />}
 </div>
 ))}
 </div>
 </div>
 </div>
 )}

 {/* Navigation Buttons */}
 <div className="flex justify-between pt-6 border-t">
 {step > 1 ? (
 <Button
 type="button"
 variant="outline"
 onClick={() => setStep(step - 1)}
 disabled={loading}
 >
 Back
 </Button>
 ) : (
 <div />
 )}
 
 <Button
 type="submit"
 disabled={loading}
 className="min-w-[120px]"
 >
 {loading ? (
 'Saving...'
 ) : step === 3 ? (
 'Complete'
 ) : (
 <>
 Next <ChevronRight className="w-4 h-4 ml-1" />
 </>
 )}
 </Button>
 </div>
 </form>
 </CardContent>
 </Card>
 </div>
 </div>
 );
};

export default EnhancedOnboarding;
